'use client'

import React, { useEffect, useRef, useState } from 'react'
import type { Editor, Component } from 'grapesjs'

interface GrapesJSTraitPanelProps {
  editor: Editor | null
}

const GrapesJSTraitPanel: React.FC<GrapesJSTraitPanelProps> = ({ editor }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [selectedComponent, setSelectedComponent] = useState<Component | null>(
    null
  )

  useEffect(() => {
    if (!editor || !containerRef.current) return

    // Function to handle component selection changes
    const handleComponentSelection = (component: Component | null) => {
      setSelectedComponent(component)
      updateTraitPanel(component)
    }

    // Function to update the trait panel content
    const updateTraitPanel = (component: Component | null) => {
      if (!containerRef.current) return

      containerRef.current.innerHTML = ''

      try {
        // Get the trait manager
        const traitManager = editor.TraitManager

        if (component) {
          const traitManagerEl = traitManager.render()

          // Append the trait manager element to our container
          containerRef.current.appendChild(traitManagerEl)
        } else {
          containerRef.current.innerHTML = `
            <div class="flex items-center justify-center h-full text-gray-400 p-4">
              <div class="text-center">
                <div class="text-sm">No element selected</div>
                <div class="text-xs mt-1 opacity-60">Click on an element to edit its properties</div>
              </div>
            </div>
          `
        }

        addTraitsPanelStyling()
      } catch (error) {
        console.error('Error rendering trait panel:', error)
        if (containerRef.current) {
          containerRef.current.innerHTML = `<div class="flex items-center justify-center h-full text-red-400">
              Error loading trait panel: ${error}
            </div>`
        }
      }
    }

    const onComponentSelected = (component: Component) => {
      handleComponentSelection(component)
    }

    const onComponentDeselected = () => {
      handleComponentSelection(null)
    }

    const onCustomSelectionEvent = (event: Event) => {
      const customEvent = event as CustomEvent
      const component = customEvent.detail?.component
      if (component) {
        handleComponentSelection(component)
      }
    }

    const onCustomDeselectionEvent = () => {
      handleComponentSelection(null)
    }

    editor.on('component:selected', onComponentSelected)
    editor.on('component:deselected', onComponentDeselected)

    window.addEventListener('gjs:component:selected', onCustomSelectionEvent)
    window.addEventListener(
      'gjs:component:deselected',
      onCustomDeselectionEvent
    )

    const currentSelected = editor.getSelected()
    if (currentSelected) {
      handleComponentSelection(currentSelected)
    } else {
      updateTraitPanel(null)
    }

    // Clean up on unmount
    return () => {
      editor.off('component:selected', onComponentSelected)
      editor.off('component:deselected', onComponentDeselected)

      window.removeEventListener(
        'gjs:component:selected',
        onCustomSelectionEvent
      )
      window.removeEventListener(
        'gjs:component:deselected',
        onCustomDeselectionEvent
      )

      if (containerRef.current) {
        containerRef.current.innerHTML = ''
      }
      // Remove custom styling
      const styleEl = document.getElementById('gjs-traits-custom-style')
      if (styleEl) styleEl.remove()
    }
  }, [editor])

  // Function to add custom styling for the traits panel
  const addTraitsPanelStyling = () => {
    // Remove any existing styling first
    const existingStyle = document.getElementById('gjs-traits-custom-style')
    if (existingStyle) existingStyle.remove()

    // Create style element
    const styleEl = document.createElement('style')
    styleEl.id = 'gjs-traits-custom-style'

    // Set the CSS styles to match the design in the image
    styleEl.innerHTML = `
      /* Main container styling */
      .gjs-trt-traits {
        background-color: #141414 !important;
        color: white;
        font-family: sans-serif;
        padding: 10px;
        width: 100%;
        
      }
      
      /* Remove any default padding/margins */
      .gjs-trt-trait__wrp {
        // margin-bottom: 30px;
        padding: 0;
        width: 100%;
      }
      
      /* Trait item */
      .gjs-trt-trait {
        display: block;
        width: 100% !important;
      }
      
      /* Trait label */
      .gjs-label-wrp {
        margin-bottom: 12px;
        text-align: left;
        width: 100%;
      }
      
      .gjs-label {
        font-size: 16px;
        font-weight: normal;
        color: white;
        text-align: left;
        display: block;
        width: 100%;
      }
      
      /* Input field wrapper */
      .gjs-field-wrp {
        width: 100%;
      }
      
      /* Input field */
      .gjs-field {
        // background-color: #1E1E1E !important;
        background-color: transparent !important;
        border: none;
        border-radius: 8px;
        overflow: hidden;
        width: 100%;
      }
      
      /* All types of inputs */
      .gjs-field input,
      .gjs-field select,
      .gjs-field textarea {
        // background-color: #1E1E1E;
        background-color: #1D2126;
        color: #FFFFFF;
        border: none;
        padding: 12px 16px;
        width: 100%;
        height: 40px;
        font-size: 14px;
        border-radius: 8px;
      }


      
      .gjs-field select {
        background-image: url('data:image/svg+xml;utf8,<svg fill="none" height="18" viewBox="0 0 20 20" width="18" xmlns="http://www.w3.org/2000/svg"><path d="M6 8l4 4 4-4" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
        background-repeat: no-repeat;
        background-position: right 12px center;
        background-size: 18px 18px;
        padding-right: 36px;
        appearance: none;
      }
      
      .gjs-field input::placeholder,
      .gjs-field select::placeholder,
      .gjs-field textarea::placeholder {
        color: #6b7280;
        width: 100%;
      }
      
      /* Fix spacing for the entire trait panel */
      .gjs-trt-traits {
        display: flex;
        flex-direction: column;
        gap: 0;
      }
      
      /* Remove any headers or extra elements */
      .gjs-trt-header,
      .gjs-trt-traits > div:not(.gjs-trt-trait__wrp) {
        display: none;
      }
      
      /* Override any default paddings */
      .gjs-one-bg {
        // background-color: #141414 !important;
        background-color: #2D3239 !important;
      }
      
      .gjs-two-color {
        color: white !important;
      }
      
      /* Ensure proper spacing between traits */
      .gjs-trt-trait__wrp:not(:last-child) {
        margin-bottom: 10px;
      }

      
      
      /* Adjust the layout to match the image */
      .gjs-trt-trait__wrp {
        padding: 0 0px;
      }
      
      /* Style for specific traits if needed */
      .gjs-trt-trait__wrp-id,
      .gjs-trt-trait__wrp-title {
        /* Specific styling for ID and Title fields if needed */
      }
    `

    // Add style to document
    document.head.appendChild(styleEl)

    // Clean up any existing title, as we don't want it for this design
    setTimeout(() => {
      const existingTitles = containerRef.current?.querySelectorAll(
        '.gjs-traits-panel__title'
      )
      existingTitles?.forEach(title => title.remove())
    }, 50)
  }

  return (
    <div
      ref={containerRef}
      className='h-full overflow-auto bg-[#1E1E1E] p-0 text-white'
    >
      {!editor && (
        <div className='flex h-full items-center justify-center text-gray-400'>
          Editor not initialized
        </div>
      )}
    </div>
  )
}

export default GrapesJSTraitPanel
